import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/Shared';
import { Navbar } from '../components/Shared';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart3, TrendingDown, Users, DollarSign, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Funnel step display names
const STEP_LABELS = {
  invite_sent: 'Invites Sent',
  invite_opened: 'Invites Opened',
  invite_accepted: 'Signups',
  onboarding_completed: 'Onboarded',
  checkout_started: 'Checkout Started',
  payment_succeeded: 'Paid',
  payment_failed: 'Payment Failed'
};

// Step colors
const STEP_COLORS = {
  invite_sent: 'bg-blue-500',
  invite_opened: 'bg-cyan-500',
  invite_accepted: 'bg-teal-500',
  onboarding_completed: 'bg-green-500',
  checkout_started: 'bg-yellow-500',
  payment_succeeded: 'bg-emerald-600',
  payment_failed: 'bg-red-500'
};

function FunnelBar({ step, count, maxCount, conversionRate }) {
  const width = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const label = STEP_LABELS[step] || step;
  const color = STEP_COLORS[step] || 'bg-neutral-400';
  const isFailed = step === 'payment_failed';
  
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1">
        <span className={`text-sm font-medium ${isFailed ? 'text-red-600' : 'text-neutral-700'}`}>
          {label}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-neutral-900">{count}</span>
          {conversionRate !== null && !isFailed && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              conversionRate >= 70 ? 'bg-green-100 text-green-700' :
              conversionRate >= 50 ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {conversionRate}%
            </span>
          )}
        </div>
      </div>
      <div className="h-8 bg-neutral-100 rounded-lg overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500 rounded-lg`}
          style={{ width: `${Math.max(width, 2)}%` }}
        />
      </div>
    </div>
  );
}

function DropOffAlert({ dropOff }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
      <TrendingDown className="text-red-500 mt-0.5" size={18} />
      <div>
        <div className="text-sm font-medium text-red-800">
          {STEP_LABELS[dropOff.from_step]} → {STEP_LABELS[dropOff.to_step]}
        </div>
        <div className="text-xs text-red-600">
          {dropOff.conversion_rate}% conversion • {dropOff.lost_count} users lost
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsDashboard() {
  const { user, token } = useAuth();
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  
  const fetchFunnel = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await fetch(`${API}/api/admin/conversion-funnel?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error(res.status === 403 ? 'Admin access required' : 'Failed to load funnel data');
      }
      
      const data = await res.json();
      setFunnel(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (token) {
      fetchFunnel();
    }
  }, [token, days]);
  
  // Calculate summary metrics
  const metrics = funnel ? {
    totalInvites: funnel.funnel.find(s => s.step === 'invite_sent')?.count || 0,
    totalPaid: funnel.funnel.find(s => s.step === 'payment_succeeded')?.count || 0,
    totalFailed: funnel.funnel.find(s => s.step === 'payment_failed')?.count || 0,
    overallConversion: (() => {
      const invites = funnel.funnel.find(s => s.step === 'invite_sent')?.count || 0;
      const paid = funnel.funnel.find(s => s.step === 'payment_succeeded')?.count || 0;
      return invites > 0 ? Math.round((paid / invites) * 100) : 0;
    })()
  } : null;
  
  // Get max count for bar scaling (excluding payment_failed)
  const maxCount = funnel 
    ? Math.max(...funnel.funnel.filter(s => s.step !== 'payment_failed').map(s => s.count))
    : 0;
  
  if (!user || user.role !== 'admin' && user.role !== 'vendor') {
    return (
      <div>
        <Navbar />
        <div className="mx-auto max-w-4xl px-4 py-8">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
            <h2 className="text-lg font-semibold text-amber-800">Admin Access Required</h2>
            <p className="text-amber-700 mt-2">You need admin or vendor role to view analytics.</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <Navbar />
      <div className="mx-auto max-w-5xl px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Pilot Conversion Funnel</h1>
            <p className="text-neutral-500 mt-1">Track user journey from invite to payment</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-2 border rounded-lg text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <Button 
              variant="outline" 
              onClick={fetchFunnel}
              disabled={loading}
              className="gap-2"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}
        
        {loading && !funnel && (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full mx-auto" />
            <p className="text-neutral-500 mt-4">Loading funnel data...</p>
          </div>
        )}
        
        {funnel && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{metrics.totalInvites}</div>
                      <div className="text-xs text-neutral-500">Invites Sent</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign size={20} className="text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{metrics.totalPaid}</div>
                      <div className="text-xs text-neutral-500">Paid Users</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <BarChart3 size={20} className="text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{metrics.overallConversion}%</div>
                      <div className="text-xs text-neutral-500">Conversion Rate</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <TrendingDown size={20} className="text-red-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{metrics.totalFailed}</div>
                      <div className="text-xs text-neutral-500">Failed Payments</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Funnel Visualization */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Conversion Funnel</CardTitle>
              </CardHeader>
              <CardContent>
                {funnel.funnel.map((step) => (
                  <FunnelBar
                    key={step.step}
                    step={step.step}
                    count={step.count}
                    maxCount={maxCount}
                    conversionRate={step.conversion_rate}
                  />
                ))}
              </CardContent>
            </Card>
            
            {/* Drop-off Alerts */}
            {funnel.drop_off_points?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                    <TrendingDown size={20} />
                    Drop-off Points
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {funnel.drop_off_points.map((dropOff, i) => (
                    <DropOffAlert key={i} dropOff={dropOff} />
                  ))}
                </CardContent>
              </Card>
            )}
            
            {/* Period Info */}
            <div className="text-center text-xs text-neutral-400 mt-6">
              Data from {new Date(funnel.period_start).toLocaleDateString()} to {new Date(funnel.period_end).toLocaleDateString()}
              {' • '}{funnel.total_events} total events
            </div>
          </>
        )}
      </div>
    </div>
  );
}
